// Segment 6 — The Build
import { useState } from 'react';
import { ArrowRight, ArrowLeft, ChevronDown, ShieldAlert, Clock } from 'lucide-react';

const AUTOPILOT_STEPS = [
  { min: '4m', text: `One sentence. With your mentor: "When [trigger], do [step]." Automate ONE link in the chain — smaller is better.` },
  { min: '6m', text: `Ask Claude. "Write a Google Apps Script that [your sentence]. My sheet's columns are […]. Give me numbered install steps for script.google.com and a plain-language explanation of each part."` },
  { min: '3m', text: `Install. script.google.com → New project → paste. Read the explanation aloud — you should be able to say what it does.` },
  { min: '5m', text: `Run once. Authorize with your ORG account → check the result.` },
  { min: '7m', text: `Didn't work? Normal. Paste the error back to Claude; apply the fix. This loop is the skill.` },
  { min: '3m', text: `Set the trigger (⏰ Triggers menu: on form submit / time-driven), per Claude's steps.` },
  { min: '2m', text: `Save the chat as "[Org] — Automations" — the script + explanation is your documentation.` },
];

const RECIPES = [
  {
    n: 1, title: 'THE SKILL',
    oneLiner: 'Teach Claude your recurring writing task, your way',
    duration: '~30 min',
    done: 'Run the skill live on a brand-new input.',
    steps: [
      { min: '3m', text: `Gather your gold. Open 2–3 of your best past examples of this output (real docs/emails).` },
      { min: '2m', text: `Open the skill builder. In your org's Claude workspace: Customize → Skills → + → Create skill.` },
      { min: '8m', text: `Teach it. Tell Claude in plain language: the task, when it recurs, the audience, your tone — paste your examples and say "these are the standard." Add your red lines (e.g., "never invent statistics — flag any number for verification"). Read what Claude writes about you; correct it.`, detail: `Vernacular variant: add to the format — "every output also includes a simple Kannada/Hindi/Tamil WhatsApp version."` },
      { min: '2m', text: `Save & switch on.` },
      { min: '8m', text: `Test on something real. New chat → ask for this month's actual output → compare against your standard.` },
      { min: '5m', text: `Iterate once. Tell Claude what missed ("too formal," "always include a beneficiary line"); update the skill.` },
      { min: '2m', text: `Name it clearly ("Donor Update — [Org]"). Your steward can share it with teammates later.` },
    ],
    nextTestGhost: `Produce one real deliverable with the skill this week, send it, and log what I edited by hand — that's version 2.`,
  },
  {
    n: 2, title: 'THE DASHBOARD',
    oneLiner: 'Your data, visible, in one tool',
    duration: '~30 min',
    done: 'The live dashboard, filtered in front of the room, plus a one-paragraph summary.',
    steps: [
      { min: '2m', gate: true, text: `Data-safety gate — Export a copy with NO beneficiary personally identifiable information — remove names/phones, or use aggregated rows. Your policy draft just made this a rule; follow it.` },
      { min: '2m', text: `Upload. New chat in your workspace → upload the cleaned CSV/XLSX.` },
      { min: '4m', text: `Ask for the dashboard. "Build an interactive dashboard from this data for [our board / program team]. Show [the 3 questions leadership always asks]."` },
      { min: '10m', text: `Iterate in plain language. "Make the monthly trend the hero" · "add a district filter" · "totals in ₹" · "explain the drop in March."` },
      { min: '5m', text: `Verify two numbers against the sheet yourself. AI + your data still needs your eyes.` },
      { min: '5m', text: `Get the words. "Write a one-paragraph summary of what this dashboard shows" — paste-ready for your next report.` },
      { min: '2m', text: `Save the chat in a Project ("[Org] — Dashboards").` },
    ],
    nextTestGhost: `Next month, upload the updated sheet to the same Project and time the refresh vs. our old process.`,
  },
  {
    n: 3, title: 'THE AUTOPILOT',
    oneLiner: 'One manual sequence, automated in tools you already own',
    duration: '~30 min',
    done: 'Trigger it live — submit the test form, watch it fire.',
    lanes: {
      'Google Workspace': AUTOPILOT_STEPS,
      'Any Google account': AUTOPILOT_STEPS,
      'Microsoft 365': AUTOPILOT_STEPS,
    },
    laneNote: `Google Workspace org: check flows.workspace.google.com (Workspace Studio — plain-English automation; use it if your plan has it) · Any Google account: Claude writes the Apps Script (the reliable default) · Microsoft 365 org: Power Automate. Steps below show the Any-Google-account lane — Lanes A and C mirror these steps in their own tool.`,
    nextTestGhost: `Let it run one real week; note every fire and every miss; bring the misses back to the Project.`,
  },
  {
    n: 4, title: 'THE ANALYST',
    oneLiner: 'Claude reads the one spreadsheet that matters',
    duration: '~30 min',
    done: "One genuine insight the org didn't know this morning.",
    steps: [
      { min: '2m', gate: true, text: `Data-safety gate — Strip beneficiary PII from the working copy.` },
      { min: '3m', text: `Choose your lane. Microsoft 365 org with desktop Excel → install "Claude for Excel" from AppSource, sign in with a workspace seat. Everyone else → upload the file to a new chat (don't burn minutes on add-ins).` },
      { min: '6m', text: `First pass. "Explain this workbook like I'm new here: what each sheet does, what depends on what, what looks fragile or inconsistent."` },
      { min: '8m', text: `Interrogate. Ask the three questions you've always had ("Which program costs most per beneficiary?" "Which donors lapsed?" "Where do these totals disagree?"). Chase one surprising answer.` },
      { min: '4m', text: `Verify two claims against the cells yourself.` },
      { min: '5m', text: `Get the deliverable. "Write a one-page brief of findings for our leadership, with the 3 actions you'd recommend."` },
      { min: '2m', text: `Save to a Project ("[Org] — Analysis").` },
    ],
    nextTestGhost: `Run the same interrogation on next quarter's version; keep the brief as our recurring template.`,
  },
  {
    n: 5, title: 'THE ORG BRAIN',
    oneLiner: 'A workspace that already knows your organization',
    duration: '~15–30 min',
    chip: 'Guaranteed win · Also the fallback',
    done: 'Ask it something org-specific, live, and hear your own voice back.',
    steps: [
      { min: '2m', text: `Create a Project in your workspace: "[Org] — Brain."` },
      { min: '6m', text: `Feed it the canon. Mission blurb · programs one-pager · 2 best past outputs · today's policy draft. (No beneficiary PII.)` },
      { min: '5m', text: `Write the instructions with your mentor: "You are [org]'s assistant. Our tone is [x]. Our audiences are [donors / CSR committees / field teams]. Always [red lines — e.g., flag unverified numbers]. Our programs are […]."` },
      { min: '5m', text: `Test with three real asks: a donor thank-you · a program summary for a CSR committee · a simple-Kannada/Hindi WhatsApp update.` },
      { min: '4m', text: `Fix the instructions based on what came out wrong. That loop = managing AI.` },
      { min: '3m', text: `Invite the team. Steward fills the remaining seats; show one colleague where the Brain lives.` },
    ],
    nextTestGhost: `For one week, start every AI task inside the Brain, and add one document to it every Friday.`,
  },
];

const RECIPE_TABLE = [
  { need: '"Every funder letter/report takes forever and sounds different each time"', recipe: '1 · The Skill' },
  { need: '"We have data in sheets but leadership never sees it clearly"',            recipe: '2 · The Dashboard' },
  { need: '"The same manual sequence eats hours every week"',                        recipe: '3 · The Autopilot' },
  { need: "\"We have one important spreadsheet we don't fully understand\"",          recipe: '4 · The Analyst' },
  { need: '"We\'re new to all of this / needs are everywhere"',                      recipe: '5 · The Org Brain' },
  { need: '"Our comms must exist in Kannada/Hindi/Tamil too"',                       recipe: '1 · The Skill (vernacular variant)' },
];

const STUCK_TIPS = [
  'Scope too big? Automate ONE link in the chain',
  'Error message? Paste it back to Claude — that loop IS the skill',
  'Not landing by 1:15? Switch to the Org Brain — 15 minutes, guaranteed real thing',
];

const SectionHeading = ({ chip, title }) => (
  <div className="mb-5">
    {chip && (
      <span className="inline-block text-xs font-bold uppercase tracking-[0.14em] text-brand-orange mb-2">
        {chip}
      </span>
    )}
    <h2 className="text-2xl font-bold text-ink leading-tight">{title}</h2>
  </div>
);

/* ── single step with optional detail accordion ── */
const StepItem = ({ step, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`recipe-step-${index + 1}`}
      className={`rounded-xl border p-4 ${
        step.gate
          ? 'border border-red-200 border-l-4 border-l-red-500 bg-red-50/70'
          : 'border-black/[0.07] bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        {step.gate ? (
          <span className="w-8 h-8 rounded-lg bg-red-500 text-white flex items-center justify-center shrink-0">
            <ShieldAlert size={16} strokeWidth={2.2} />
          </span>
        ) : (
          <span className="w-8 h-8 rounded-lg bg-brand-teal text-white font-bold text-sm flex items-center justify-center shrink-0">
            {index + 1}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-ink-muted">
              <Clock size={11} /> {step.min}
            </span>
          </div>
          <p className={`text-[15px] leading-snug mt-0.5 ${step.gate ? 'font-bold text-red-700' : 'font-medium text-ink'}`}>
            {step.text}
          </p>
          {!step.gate && step.detail && (
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid={`recipe-step-${index + 1}-toggle`}
              className="inline-flex items-center gap-1 text-xs font-semibold text-brand-teal mt-2 hover:underline"
            >
              {open ? 'Hide detail' : 'Show detail'}
              <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
          )}
          {open && step.detail && (
            <p className="text-sm text-ink-secondary leading-relaxed mt-2 italic">{step.detail}</p>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── full recipe template (expanded card) ── */
const RecipeTemplate = ({ recipe, onBack }) => {
  const laneNames = recipe.lanes ? Object.keys(recipe.lanes) : null;
  const [lane, setLane] = useState(laneNames ? laneNames[0] : null);
  const [nextTest, setNextTest] = useState('');
  const activeSteps = recipe.lanes ? recipe.lanes[lane] : recipe.steps;

  return (
    <div data-testid={`recipe-template-${recipe.n}`} className="rounded-3xl bg-white border border-black/[0.07] shadow-sm p-6 md:p-8">
      <button
        onClick={onBack}
        data-testid="recipe-back-btn"
        className="inline-flex items-center gap-2 text-sm font-semibold text-ink-secondary hover:text-ink mb-6 transition-colors"
      >
        <ArrowLeft size={16} /> All recipes
      </button>

      <div className="flex items-center gap-3 mb-1">
        <span className="w-9 h-9 rounded-xl bg-brand-orange text-white font-bold flex items-center justify-center">{recipe.n}</span>
        <h3 className="text-2xl font-bold text-ink">{recipe.title}</h3>
      </div>
      <p className="text-ink-secondary mb-3">{recipe.oneLiner}</p>
      {recipe.chip && (
        <span className="inline-block text-[11px] font-bold uppercase tracking-wide text-brand-teal bg-brand-teal/10 rounded-full px-3 py-1 mb-6">
          {recipe.chip}
        </span>
      )}

      {/* Lane chooser */}
      {laneNames && (
        <div className="mb-6" data-testid="autopilot-lane-chooser">
          <p className="text-sm font-semibold text-ink mb-2">Pick your lane:</p>
          <div className="flex flex-wrap gap-2">
            {laneNames.map((l) => (
              <button
                key={l}
                onClick={() => setLane(l)}
                data-testid={`lane-${l.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-colors ${
                  lane === l
                    ? 'bg-brand-teal border-brand-teal text-white'
                    : 'bg-white border-black/10 text-ink-secondary hover:border-brand-teal/50'
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          {recipe.laneNote && (
            <p className="text-xs text-ink-muted mt-3 leading-relaxed">{recipe.laneNote}</p>
          )}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Left — steps */}
        <div className="flex-1 min-w-0 space-y-3">
          {activeSteps.map((s, i) => <StepItem key={`${lane}-${i}`} step={s} index={i} />)}
        </div>

        {/* Right — Stuck + Done (desktop) */}
        <aside className="w-full lg:w-64 shrink-0 space-y-4">
          <div className="bg-cream rounded-2xl border-l-4 border-brand-teal p-5" data-testid="recipe-stuck-card">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-brand-teal mb-3">Stuck? Try this</p>
            <ul className="space-y-2.5">
              {STUCK_TIPS.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink-secondary leading-relaxed">
                  <span className="text-brand-teal font-bold mt-px shrink-0">→</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-brand-teal/[0.06] rounded-2xl border border-brand-teal/20 p-5" data-testid="recipe-done-card">
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-brand-teal mb-2">Done looks like</p>
            <p className="text-sm text-ink leading-relaxed">{recipe.done}</p>
          </div>
        </aside>
      </div>

      {/* Bottom — Next test */}
      <div className="mt-8 pt-6 border-t border-black/[0.06]">
        <label className="block text-xs font-bold uppercase tracking-[0.14em] text-brand-orange mb-2">Your next test</label>
        <textarea
          value={nextTest}
          onChange={(e) => setNextTest(e.target.value)}
          placeholder={recipe.nextTestGhost}
          rows={3}
          data-testid="recipe-next-test-input"
          className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/40 transition-colors resize-y"
        />
        <p className="text-xs text-ink-muted mt-2">Write it here, then copy it into your Claude Project.</p>
      </div>
    </div>
  );
};

/* ── recipe grid card ── */
const RecipeCard = ({ recipe, onOpen }) => (
  <button
    onClick={onOpen}
    data-testid={`recipe-card-${recipe.n}`}
    className="group text-left rounded-2xl bg-white border border-black/[0.07] shadow-sm p-6 hover:border-brand-orange/50 hover:shadow-md transition-all"
  >
    <div className="flex items-center justify-between mb-3">
      <span className="w-9 h-9 rounded-xl bg-brand-teal text-white font-bold flex items-center justify-center">{recipe.n}</span>
      <ArrowRight size={20} className="text-brand-orange group-hover:translate-x-1 transition-transform" strokeWidth={2.4} />
    </div>
    <h3 className="text-lg font-bold text-ink mb-1">{recipe.title}</h3>
    <p className="text-sm text-ink-secondary leading-snug mb-3">{recipe.oneLiner}</p>
    {recipe.chip && (
      <span className="inline-block text-[11px] font-bold uppercase tracking-wide text-brand-teal bg-brand-teal/10 rounded-full px-3 py-1 mb-2">
        {recipe.chip}
      </span>
    )}
    <p className="text-xs font-bold text-ink-muted">{recipe.duration}</p>
  </button>
);

const Segment6 = () => {
  const [commitment, setCommitment] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openRecipe, setOpenRecipe] = useState(null);

  const active = RECIPES.find((r) => r.n === openRecipe);

  return (
    <div data-testid="segment-6">
      <h1 className="text-4xl md:text-5xl font-bold text-ink mb-3 leading-tight">The Build</h1>
      <p className="text-lg text-ink-secondary mb-10">
        Pick a recipe, build something real, keep it forever.
      </p>

      <div className="space-y-14">

        {/* 1 — Scoping */}
        <section data-testid="scoping-section">
          <SectionHeading title="Scoping — write your commitment line" />
          <input
            type="text"
            value={commitment}
            onChange={(e) => setCommitment(e.target.value)}
            placeholder="We are building [recipe] for [task]. Done means [demo]."
            data-testid="commitment-input"
            className="w-full rounded-xl border border-black/10 bg-white px-4 py-3.5 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal/40 transition-colors mb-4"
          />

          {/* Recipe Picker accordion */}
          <div className="rounded-xl border border-black/[0.07] overflow-hidden bg-white" data-testid="recipe-picker-accordion">
            <button
              onClick={() => setPickerOpen((o) => !o)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50/80 transition-colors"
            >
              <span className="font-semibold text-ink text-[15px]">Recipe Picker — which one fits?</span>
              <ChevronDown size={16} className={`text-ink-secondary transition-transform shrink-0 ${pickerOpen ? 'rotate-180' : ''}`} />
            </button>
            {pickerOpen && (
              <div className="px-5 pb-5 pt-1 bg-gray-50/60 border-t border-black/[0.05]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-ink-muted">
                      <th className="py-2 font-semibold">If your need sounds like…</th>
                      <th className="py-2 font-semibold">Build this</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RECIPE_TABLE.map((row, i) => (
                      <tr key={i} className="border-t border-black/[0.06]">
                        <td className="py-2.5 pr-4 text-ink">{row.need}</td>
                        <td className="py-2.5 font-semibold text-brand-teal">{row.recipe}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* 2 — Pick your recipe */}
        <section data-testid="recipes-section">
          <SectionHeading title="Pick your recipe" />
          {active ? (
            <RecipeTemplate recipe={active} onBack={() => setOpenRecipe(null)} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {RECIPES.map((r) => (
                <RecipeCard key={r.n} recipe={r} onOpen={() => setOpenRecipe(r.n)} />
              ))}
            </div>
          )}
        </section>

        {/* 3 — Peer review */}
        <section data-testid="peer-review-section">
          <SectionHeading chip="8 min" title="Peer review" />
          <div className="rounded-2xl bg-white border border-black/[0.07] shadow-sm p-6">
            <p className="text-ink leading-relaxed">
              Swap with the nearest org. Two comments each:{' '}
              <strong className="text-ink">what's clear</strong> ·{' '}
              <strong className="text-ink">what would you add</strong>. Apply one fix.
            </p>
          </div>
        </section>

        {/* 4 — Freeze & save */}
        <section data-testid="freeze-save-section">
          <SectionHeading chip="4 min" title="Freeze & save" />
          <div className="rounded-2xl bg-brand-teal/[0.06] border border-brand-teal/20 p-6">
            <p className="text-ink leading-relaxed">
              Hands off keyboards. Save everything as a Project in your workspace. Write your Next
              Test. Stop.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Segment6;
